import { useState, useEffect, useRef } from "react";
import { Crown, Sparkles, Star } from "lucide-react";
import { Button } from "./ui/button";

const MatrixUnlock = ({ isOpen, onClose, tier = "rooted" }) => {
  const canvasRef = useRef(null);
  const [showContent, setShowContent] = useState(false);
  const [phase, setPhase] = useState(0); // 0: matrix + bursts, 1: content phase, 2: final burst, 3: final content
  const [glassesOn, setGlassesOn] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [showCommitment, setShowCommitment] = useState(false);
  const [committed, setCommitted] = useState(false);
  const [burstCount, setBurstCount] = useState(0);
  const [finalBurstDone, setFinalBurstDone] = useState(false);
  const [showFinalContent, setShowFinalContent] = useState(false);

  // Dick Gregory quote - same for all tiers
  const dickGregoryQuote = "When the universe picks you, and you put on the magic glasses... you never see things as they're supposed to be, you see things as they are, and you can never force anyone to wear them.";

  // Tier-specific glass colors - each tier has unique color (New Lala Land Tiers)
  const glassColors = {
    seedling: { primary: "#10b981", glow: "rgba(16, 185, 129, 0.3)", name: "Emerald" },
    rooted: { primary: "#8b5cf6", glow: "rgba(139, 92, 246, 0.3)", name: "Amethyst" },
    forever_rooted: { primary: "#f59e0b", glow: "rgba(245, 158, 11, 0.3)", name: "Golden" }
  };

  const currentGlassColor = glassColors[tier] || glassColors.rooted;

  // Matrix characters - mix of spiritual symbols and characters
  const matrixChars = "アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789🦋✨🌟💫🔮🧘🏾‍♀️🌙⭐∞♾️";

  // Canvas animation effect
  useEffect(() => {
    if (!isOpen) {
      setShowContent(false);
      setPhase(0);
      setGlassesOn(false);
      setShowQuote(false);
      setShowCommitment(false);
      setCommitted(false);
      setBurstCount(0);
      setFinalBurstDone(false);
      setShowFinalContent(false);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const fontSize = 16;
    const columns = Math.floor(canvas.width / fontSize);
    const drops = Array(columns).fill(1);
    
    // Matrix rain colors based on tier (New Lala Land Tiers)
    const matrixColors = {
      seedling: { primary: "#10b981", glow: "rgba(16, 185, 129, 0.8)" },
      rooted: { primary: "#8b5cf6", glow: "rgba(139, 92, 246, 0.8)" },
      forever_rooted: { primary: "#f59e0b", glow: "rgba(245, 158, 11, 0.8)" }
    };
    
    const colors = matrixColors[tier] || matrixColors.rooted;
    const primaryColor = colors.primary;
    const glowColor = colors.glow;

    let burstProgress = 0;
    let localBurstCount = burstCount;
    let burstActive = false;
    let burstStartTime = 0;
    
    // Final burst state
    let finalBurstActive = committed && !finalBurstDone;
    let finalBurstProgress = 0;
    let finalBurstStartTime = committed ? Date.now() : 0;

    const draw = () => {
      const now = Date.now();
      
      // Determine if we're in background mode
      const isBackgroundMode = (phase >= 1 && !committed) || (committed && finalBurstDone);
      
      // Fade effect - slower when in background mode
      const fadeAmount = isBackgroundMode ? 0.15 : 0.05;
      ctx.fillStyle = `rgba(0, 0, 0, ${fadeAmount})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.font = `${fontSize}px monospace`;

      // Draw matrix rain
      const dropSpeed = isBackgroundMode ? 0.3 : 1;
      const sparseness = isBackgroundMode ? 0.7 : 0;

      for (let i = 0; i < drops.length; i++) {
        if (isBackgroundMode && Math.random() < sparseness) continue;

        const char = matrixChars[Math.floor(Math.random() * matrixChars.length)];
        const alpha = isBackgroundMode ? 0.3 : 1;
        
        if (Math.random() > 0.95) {
          ctx.shadowBlur = isBackgroundMode ? 10 : 20;
          ctx.shadowColor = glowColor;
          ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
        } else {
          ctx.shadowBlur = 0;
          ctx.fillStyle = isBackgroundMode 
            ? `${primaryColor}4D` // 30% opacity hex
            : primaryColor;
        }

        ctx.fillText(char, i * fontSize, drops[i] * fontSize);

        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i] += dropSpeed;
      }

      // Initial burst effects (2 bursts before showing content)
      if (phase === 0 && burstActive) {
        const burstDuration = 1500;
        const elapsed = now - burstStartTime;
        burstProgress = Math.min(elapsed / burstDuration, 1.2);

        drawBurstEffect(ctx, canvas, burstProgress, primaryColor, glowColor);

        if (burstProgress >= 1.2) {
          burstActive = false;
          localBurstCount++;
          setBurstCount(localBurstCount);
          
          if (localBurstCount >= 2) {
            setPhase(1);
          }
        }
      }

      // Final burst effect (after clicking Yes, I Accept)
      if (committed && !finalBurstDone) {
        const burstDuration = 1500;
        const elapsed = now - finalBurstStartTime;
        finalBurstProgress = Math.min(elapsed / burstDuration, 1.2);

        drawBurstEffect(ctx, canvas, finalBurstProgress, primaryColor, glowColor);

        if (finalBurstProgress >= 1.2) {
          setFinalBurstDone(true);
          setTimeout(() => setShowFinalContent(true), 500);
        }
      }
    };

    // Helper function to draw burst effect
    const drawBurstEffect = (ctx, canvas, progress, primaryColor, glowColor) => {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const maxRadius = Math.sqrt(centerX * centerX + centerY * centerY);
      const currentRadius = progress * maxRadius;

      // Draw expanding circle/shatter effect
      ctx.beginPath();
      ctx.arc(centerX, centerY, currentRadius * 0.8, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(0, 0, 0, 0.2)";
      ctx.fill();

      // Draw shatter/burst lines (rays)
      ctx.strokeStyle = glowColor;
      ctx.lineWidth = 3;
      ctx.shadowBlur = 20;
      ctx.shadowColor = glowColor;
      
      for (let i = 0; i < 16; i++) {
        const angle = (i / 16) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(
          centerX + Math.cos(angle) * currentRadius * 1.5,
          centerY + Math.sin(angle) * currentRadius * 1.5
        );
        ctx.stroke();
      }

      // Flying particles around the burst
      ctx.shadowBlur = 15;
      for (let i = 0; i < 30; i++) {
        const angle = Math.random() * Math.PI * 2;
        const dist = currentRadius * (0.3 + Math.random() * 0.7);
        const x = centerX + Math.cos(angle) * dist;
        const y = centerY + Math.sin(angle) * dist;
        const char = matrixChars[Math.floor(Math.random() * matrixChars.length)];
        
        ctx.fillStyle = primaryColor;
        ctx.fillText(char, x, y);
      }

      // Reset shadow
      ctx.shadowBlur = 0;
    };

    const interval = setInterval(draw, 33);

    // Start first burst after 1.5 seconds (only if phase 0)
    const burst1Timer = setTimeout(() => {
      if (burstCount === 0 && phase === 0) {
        burstActive = true;
        burstStartTime = Date.now();
      }
    }, 1500);

    // Start second burst after first completes
    const burst2Timer = setTimeout(() => {
      if (burstCount === 1 && phase === 0) {
        burstActive = true;
        burstStartTime = Date.now();
      }
    }, 3500);

    return () => {
      clearInterval(interval);
      clearTimeout(burst1Timer);
      clearTimeout(burst2Timer);
    };
  }, [isOpen, phase, burstCount, committed, finalBurstDone, tier]);

  // Handle content reveal after bursts complete
  useEffect(() => {
    if (!isOpen || phase !== 1) return;

    // Show glasses
    const glassesTimer = setTimeout(() => {
      setGlassesOn(true);
    }, 500);

    // Show quote
    const quoteTimer = setTimeout(() => {
      setShowQuote(true);
    }, 1500);

    // Show commitment
    const commitmentTimer = setTimeout(() => {
      setShowCommitment(true);
    }, 3500);

    return () => {
      clearTimeout(glassesTimer);
      clearTimeout(quoteTimer);
      clearTimeout(commitmentTimer);
    };
  }, [isOpen, phase]);

  const handleCommitment = () => {
    setCommitted(true);
    // Final burst will trigger automatically via the canvas effect
  };

  if (!isOpen) return null;

  // New Lala Land Tier Naming - $5.55/mo Rooted, $111.11 Forever Rooted
  const tierNames = {
    seedling: "Seedling",
    rooted: "Rooted",
    forever_rooted: "Forever Rooted"
  };

  const tierColors = {
    seedling: "from-emerald-400 to-green-500",
    rooted: "from-violet-400 to-purple-500",
    forever_rooted: "from-amber-400 to-orange-500"
  };

  const tierIcons = {
    seedling: "🌱",
    rooted: "🌿",
    forever_rooted: "👑"
  };
  
  const tierPricing = {
    seedling: "Free",
    rooted: "$5.55/month",
    forever_rooted: "$111.11 one-time"
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden">
      {/* Matrix Canvas Background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ background: "black" }}
      />

      {/* Dark overlay for better text readability when showing content */}
      {((phase >= 1 && !committed) || (committed && finalBurstDone)) && (
        <div className="absolute inset-0 bg-black/60 transition-opacity duration-1000" />
      )}

      {/* Magic Glasses Overlay - Shows after initial bursts complete */}
      {phase >= 1 && !committed && (
        <div className="absolute inset-0 flex flex-col items-center justify-start overflow-y-auto py-6 px-4 pb-20">
          <div className="text-center w-full max-w-md flex-shrink-0">
            {/* Magic Glasses Animation */}
            <div className={`transition-all duration-1000 ${glassesOn ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}>
              {/* Glasses SVG */}
              <div className="relative mx-auto mb-4 w-32 h-20 md:w-40 md:h-24">
                <svg viewBox="0 0 200 100" className="w-full h-full">
                  {/* Left lens */}
                  <ellipse cx="55" cy="50" rx="45" ry="35" fill="none" stroke={currentGlassColor.primary} strokeWidth="4" className="animate-pulse" />
                  <ellipse cx="55" cy="50" rx="40" ry="30" fill={currentGlassColor.glow} />
                  {/* Right lens */}
                  <ellipse cx="145" cy="50" rx="45" ry="35" fill="none" stroke={currentGlassColor.primary} strokeWidth="4" className="animate-pulse" />
                  <ellipse cx="145" cy="50" rx="40" ry="30" fill={currentGlassColor.glow} />
                  {/* Bridge */}
                  <path d="M 95 50 Q 100 40 105 50" fill="none" stroke={currentGlassColor.primary} strokeWidth="4" />
                  {/* Temples */}
                  <line x1="10" y1="35" x2="0" y2="30" stroke={currentGlassColor.primary} strokeWidth="3" />
                  <line x1="190" y1="35" x2="200" y2="30" stroke={currentGlassColor.primary} strokeWidth="3" />
                  {/* Sparkle effects */}
                  <circle cx="30" cy="35" r="3" fill="#fff" className="animate-ping" />
                  <circle cx="170" cy="35" r="3" fill="#fff" className="animate-ping" style={{animationDelay: '0.5s'}} />
                </svg>
                {/* Glow effect */}
                <div 
                  className="absolute inset-0 blur-xl rounded-full animate-pulse" 
                  style={{ backgroundColor: currentGlassColor.glow }}
                />
              </div>
              
              <h2 className="text-xl md:text-2xl font-bold mb-2" style={{ color: currentGlassColor.primary }}>
                ✨ The {currentGlassColor.name} Glasses
              </h2>
              <p className="text-white/70 text-sm md:text-base mb-4">
                The universe has chosen you...
              </p>
            </div>

            {/* Dick Gregory Quote */}
            <div className={`transition-all duration-1000 delay-300 ${showQuote ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="bg-black/60 backdrop-blur-md border border-white/20 rounded-xl p-5 mb-4 shadow-2xl">
                <p className="text-base md:text-lg text-white italic leading-relaxed mb-3">
                  &ldquo;{dickGregoryQuote}&rdquo;
                </p>
                <p className="text-sm md:text-base font-semibold" style={{ color: currentGlassColor.primary }}>
                  — Dick Gregory
                </p>
              </div>
            </div>

            {/* Commitment Section */}
            <div className={`transition-all duration-1000 ${showCommitment ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="bg-black/70 backdrop-blur-md border border-white/20 rounded-xl p-5 mb-4 shadow-2xl">
                <h3 className="text-lg md:text-xl text-white font-semibold mb-3">
                  🦋 Your Sacred Commitment
                </h3>
                <p className="text-white/90 text-sm md:text-base leading-relaxed mb-4">
                  By accepting these gifts, I commit to:
                </p>
                <ul className="text-left space-y-3 mb-5 text-sm md:text-base">
                  <li className="flex items-start gap-3 text-white">
                    <span style={{ color: currentGlassColor.primary }} className="mt-0.5 text-lg">✓</span>
                    <span>Focusing on my own spiritual journey</span>
                  </li>
                  <li className="flex items-start gap-3 text-white">
                    <span style={{ color: currentGlassColor.primary }} className="mt-0.5 text-lg">✓</span>
                    <span>Using this knowledge responsibly and with love</span>
                  </li>
                  <li className="flex items-start gap-3 text-white">
                    <span style={{ color: currentGlassColor.primary }} className="mt-0.5 text-lg">✓</span>
                    <span>Honoring my path while respecting others</span>
                  </li>
                </ul>
                <Button
                  onClick={handleCommitment}
                  className={`w-full py-5 text-base md:text-lg rounded-full text-white font-bold shadow-lg ${
                    tier === 'seedling' ? 'bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 shadow-emerald-500/30' :
                    tier === 'rooted' ? 'bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 shadow-violet-500/30' :
                    'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-amber-500/30'
                  }`}
                >
                  Yes, I Accept 🙏🏾
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Final Content Overlay - Shows after final burst completes */}
      <div 
        className={`absolute inset-0 flex items-center justify-center transition-all duration-1000 ${
          showFinalContent && committed && finalBurstDone ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="text-center px-6 max-w-md animate-scale-in relative z-10">
          {/* Glowing Icon */}
          <div className={`w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br ${tierColors[tier]} flex items-center justify-center animate-pulse-glow`}>
            <span className="text-5xl">{tierIcons[tier]}</span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 animate-fade-in-up">
            You&apos;ve Broken Through!
          </h1>

          {/* Tier Badge */}
          <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r ${tierColors[tier]} mb-6`}>
            <Crown className="h-5 w-5 text-white" />
            <span className="text-white font-bold text-lg">{tierNames[tier]} Unlocked</span>
            <Sparkles className="h-5 w-5 text-white" />
          </div>

          {/* Description with Pricing */}
          <p className="text-white/80 text-lg mb-2 leading-relaxed">
            Welcome to {tierNames[tier]}, beautiful soul! ✨<br />
            Your spiritual journey has reached new heights.
          </p>
          <p className="text-white/60 text-sm mb-8">
            {tierPricing[tier]}
          </p>

          {/* Features unlocked */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            {tier === "forever_rooted" && (
              <>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-white/90 text-sm">
                  🎙️ Voice Affirmations
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-white/90 text-sm">
                  🧘🏾 Guided Meditations
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-white/90 text-sm">
                  🔮 All Premium Features
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-white/90 text-sm">
                  💫 Lifetime Access
                </div>
              </>
            )}
            {tier === "rooted" && (
              <>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-white/90 text-sm">
                  📔 Journal Access
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-white/90 text-sm">
                  🎵 Sound Sanctuary
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-white/90 text-sm">
                  ✨ Custom Affirmations
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-white/90 text-sm">
                  🦋 Spirit Animals
                </div>
              </>
            )}
            {tier === "seedling" && (
              <>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-white/90 text-sm">
                  📔 Basic Journal
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-white/90 text-sm">
                  🎵 Sound Samples
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-white/90 text-sm">
                  🧘🏾 Daily Meditations
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-white/90 text-sm">
                  ✨ Core Features
                </div>
              </>
            )}
          </div>

          {/* Continue Button */}
          <Button
            onClick={onClose}
            className={`px-10 py-6 text-lg rounded-full bg-gradient-to-r ${tierColors[tier]} hover:opacity-90 text-white font-semibold shadow-2xl`}
          >
            Begin Your Journey 🦋
          </Button>

          {/* Floating particles */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${3 + Math.random() * 4}s`
                }}
              >
                <Star className="h-4 w-4 text-white/30" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Skip button (always visible) */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white/50 hover:text-white text-sm transition-colors z-10"
      >
        Skip →
      </button>
    </div>
  );
};

export default MatrixUnlock;
